import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver, type Driver, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { useOptionalAuth } from '../../../lib/auth/auth-context.js';
import {
  onboardingKeys,
  useDismissGuidedTour,
  useOnboarding,
} from '../../../lib/onboarding/use-onboarding.js';
import {
  customerIdFrom,
  resolveTourStep,
  resumePathFor,
  tourStage,
  type TourStepId,
} from './resolve-step.js';
import { TourCelebration } from './tour-celebration.js';
import { TourHint } from './tour-hint.js';
import { TourResumePill } from './tour-resume-pill.js';
import { findTourTarget, TOUR_STEPS, tourProgress } from './tour-steps.js';
import { tourStorage } from './tour-storage.js';
import { TourWelcome } from './tour-welcome.js';
import { useTourDom } from './use-tour-dom.js';

/**
 * The spotlight walkthrough of a shop's first real order: find Customers, add one, open their
 * measurements, save a set, book the order — then a celebration.
 *
 * Mounted once in the app shell, for the owner only (the same person the setup checklist and
 * the guided-order card are for, and the only role allowed to dismiss it). It drives
 * `driver.js` for the "tap here" moments and its own floating hint for the "fill this in" ones;
 * see `tour-steps.ts` for why there are two.
 *
 * Nothing about progress is stored here. Each render asks `resolveTourStep` where the shop is,
 * from the server's ticks and the open screen, and the spotlight follows. What the controller
 * owns is only whether the tour is RUNNING in this tab, which is a choice, not a fact.
 *
 * Lifecycle:
 *  - `welcome`     a brand-new shop's first visit to Home: a greeting with Start and Skip.
 *  - `running`     the spotlight is following the shop through the steps.
 *  - `paused`      Escape or a tap outside the spotlight: the tour steps back to a "Resume" pill.
 *  - `celebrating` the order is booked; confetti and what to do next.
 *  - `idle`        not now. The Home card's button starts it again.
 *
 * Skip — on the welcome, the tooltip's ×, the hint or the pill — is the permanent one: it records
 * `tour_dismissed_at` for the whole shop, so it does not come back on reload or another device.
 */

export type TourStatus = 'idle' | 'welcome' | 'running' | 'paused' | 'celebrating';

interface TourApi {
  status: TourStatus;
  /** Starts the tour, or picks it up where the shop's data says it stands. */
  start: () => void;
}

const TourContext = createContext<TourApi | null>(null);

/** Null for anybody the tour is not for (everyone but the owner). */
export function useTour(): TourApi | null {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: ReactNode }): ReactElement {
  const auth = useOptionalAuth();
  const shop = auth?.shop ?? null;
  if (auth?.role !== 'owner' || !shop) return <>{children}</>;
  return (
    <OwnerTour shopId={shop.id} shopName={shop.name}>
      {children}
    </OwnerTour>
  );
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** i18n keys cannot carry dashes comfortably in every tool; the copy is keyed in camelCase. */
function copyKey(step: TourStepId): string {
  return step.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function OwnerTour({
  shopId,
  shopName,
  children,
}: {
  shopId: string;
  shopName: string;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const storage = useMemo(() => tourStorage(shopId), [shopId]);
  const onboarding = useOnboarding();
  const dismiss = useDismissGuidedTour();
  const tour = onboarding.data?.tour;

  const [status, setStatusState] = useState<TourStatus>(() => storage.runState() ?? 'idle');
  const setStatus = useCallback(
    (next: TourStatus) => {
      setStatusState(next);
      storage.setRunState(next === 'running' || next === 'paused' ? next : null);
    },
    [storage],
  );

  const running = status === 'running';
  const dom = useTourDom(status === 'running' || status === 'paused');
  const resolution = tour
    ? resolveTourStep(tour, {
        pathname,
        dialogOpen: dom.dialog !== null,
        measurementsTabOpen: dom.measurementsTabOpen,
      })
    : null;
  const stage = tour ? tourStage(tour) : null;
  const step = running && resolution?.kind === 'step' ? resolution.step : null;
  const stepDef = step ? TOUR_STEPS[step] : null;

  // --- Moving between states ------------------------------------------------------------------

  const skip = useCallback(() => {
    storage.markWelcomed();
    setStatus('idle');
    dismiss.mutate();
  }, [dismiss, setStatus, storage]);

  const pause = useCallback(() => setStatus('paused'), [setStatus]);

  const start = useCallback(() => {
    storage.markWelcomed();
    setStatus('running');
    // From Home the first step points at the navigation; a later stage needs its own screen.
    if (stage && resolution?.kind === 'off-route') {
      const path = resumePathFor(stage, storage.customerId());
      if (path) void navigate(path);
    }
  }, [navigate, resolution?.kind, setStatus, stage, storage]);

  const later = useCallback(() => {
    storage.markWelcomed();
    setStatus('idle');
  }, [setStatus, storage]);

  const finishCelebration = useCallback(() => {
    storage.markCelebrated();
    setStatus('idle');
  }, [setStatus, storage]);

  // A brand-new shop's first look at Home. A shop that already has customers was here before the
  // tour existed, and is not greeted as if it were new.
  useEffect(() => {
    if (status !== 'idle' || !tour || tour.dismissed || pathname !== '/') return;
    if (tour.customerAdded || tour.measurementSaved || tour.orderBooked) return;
    if (storage.welcomed()) return;
    setStatus('welcome');
  }, [pathname, setStatus, status, storage, tour]);

  // Skipped from another device or the Home card: stop here too.
  useEffect(() => {
    if (tour?.dismissed && (status === 'running' || status === 'paused' || status === 'welcome')) {
      setStatus('idle');
    }
  }, [setStatus, status, tour?.dismissed]);

  // The order is booked. Celebrate once the screen is clear — a bill dialog may still be up.
  useEffect(() => {
    if (resolution?.kind !== 'finished' || (status !== 'running' && status !== 'paused')) return;
    if (tour?.dismissed || storage.celebrated()) {
      setStatus('idle');
      return;
    }
    if (!dom.dialog) setStatus('celebrating');
  }, [dom.dialog, resolution?.kind, setStatus, status, storage, tour?.dismissed]);

  // Remember whose page this is, so Resume can come back to the same customer.
  useEffect(() => {
    if (!running) return;
    const id = customerIdFrom(pathname);
    if (id) storage.setCustomerId(id);
  }, [pathname, running, storage]);

  // The ticks are the server's, and none of the forms on the way know the tour exists. So while
  // it runs, every successful save anywhere — and every change of screen — asks again.
  useEffect(() => {
    if (!running) return;
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success') {
        void queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });
      }
    });
  }, [queryClient, running]);

  useEffect(() => {
    if (running) void queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });
  }, [pathname, queryClient, running]);

  // --- The spotlight --------------------------------------------------------------------------

  const driverRef = useRef<Driver | null>(null);
  // driver.js holds its callbacks for its whole life; these refs keep them pointing at the
  // current render's functions.
  const pauseRef = useRef(pause);
  const skipRef = useRef(skip);
  const bookRef = useRef(() => {});
  pauseRef.current = pause;
  skipRef.current = skip;
  bookRef.current = () => {
    const id = customerIdFrom(pathname) ?? storage.customerId();
    void navigate(id ? `/orders/new?customerId=${id}` : '/orders/new');
  };

  const spotlight = stepDef?.kind === 'spotlight' ? step : null;

  useEffect(() => {
    const current = driverRef.current;
    if (!spotlight) {
      current?.destroy();
      return;
    }
    const def = TOUR_STEPS[spotlight];
    let cancelled = false;

    const show = (element: HTMLElement | null) => {
      if (cancelled) return;
      const tourDriver =
        driverRef.current ??
        (driverRef.current = driver({
          animate: !prefersReducedMotion(),
          smoothScroll: !prefersReducedMotion(),
          allowClose: true,
          overlayColor: 'rgb(15, 23, 42)',
          overlayOpacity: 0.55,
          stagePadding: 6,
          stageRadius: 12,
          popoverOffset: 12,
          popoverClass: 'crm-tour-popover',
          showButtons: ['close'],
          // The × is the permanent "no thanks"; Escape and a tap on the dark are "not now".
          onCloseClick: () => skipRef.current(),
          onDestroyStarted: () => pauseRef.current(),
        }));
      tourDriver.highlight({
        ...(element ? { element } : {}),
        popover: {
          title: t(`onboarding.spotlight.steps.${copyKey(spotlight)}.title`),
          description: t(`onboarding.spotlight.steps.${copyKey(spotlight)}.body`),
          ...(def.side ? { side: def.side } : {}),
          ...(def.align ? { align: def.align } : {}),
          // `highlight()` hides every button unless the step asks for one.
          showButtons: ['close'],
          closeBtnLabel: t('onboarding.spotlight.skip'),
          onPopoverRender: (popover: PopoverDOM) => {
            renderFooter(popover, {
              progress: t('onboarding.spotlight.progress', tourProgress(spotlight)),
              skipLabel: t('onboarding.spotlight.skip'),
              onSkip: () => skipRef.current(),
              ...(spotlight === 'order-entry'
                ? {
                    action: {
                      label: t('onboarding.spotlight.bookOrder'),
                      onClick: () => bookRef.current(),
                    },
                  }
                : {}),
            });
          },
        },
      });
    };

    if (!def.target) {
      show(null);
      return () => {
        cancelled = true;
      };
    }

    const target = def.target;
    const found = findTourTarget(target);
    if (found) {
      show(found);
      return () => {
        cancelled = true;
      };
    }

    // Not on screen yet — a route still rendering, a tab still opening. Keep the old spotlight
    // for a beat so a quick swap glides rather than blinks, then let go of the page: a dark
    // overlay with nothing lit would leave the shop unable to tap anything.
    const observer = new MutationObserver(() => {
      const element = findTourTarget(target);
      if (element) {
        stop();
        show(element);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    const letGo = window.setTimeout(() => driverRef.current?.destroy(), 300);
    const giveUp = window.setTimeout(() => stop(), 15_000);
    function stop() {
      observer.disconnect();
      window.clearTimeout(letGo);
      window.clearTimeout(giveUp);
    }
    return () => {
      cancelled = true;
      stop();
    };
  }, [spotlight, t]);

  useEffect(() => () => driverRef.current?.destroy(), []);

  // --- What is drawn --------------------------------------------------------------------------

  const resume = useCallback(() => {
    setStatus('running');
    if (stage && resolution?.kind === 'off-route') {
      const path = resumePathFor(stage, storage.customerId());
      if (path) void navigate(path);
    }
  }, [navigate, resolution?.kind, setStatus, stage, storage]);

  const api = useMemo<TourApi>(() => ({ status, start }), [start, status]);
  const offRoute = running && resolution?.kind === 'off-route';

  return (
    <TourContext.Provider value={api}>
      {children}

      {status === 'welcome' ? (
        <TourWelcome shopName={shopName} onStart={start} onSkip={skip} onLater={later} />
      ) : null}

      {step && stepDef?.kind === 'hint' ? (
        <TourHint
          step={step}
          copyKey={copyKey(step)}
          def={stepDef}
          dialog={dom.dialog}
          onPause={pause}
          onSkip={skip}
        />
      ) : null}

      {status === 'paused' || offRoute ? <TourResumePill onResume={resume} onSkip={skip} /> : null}

      {status === 'celebrating' ? (
        <TourCelebration
          setupFinished={onboarding.data?.summary.finished ?? true}
          onClose={finishCelebration}
          onSetup={() => {
            finishCelebration();
            void navigate('/setup');
          }}
        />
      ) : null}
    </TourContext.Provider>
  );
}

/**
 * driver.js draws its own popover; its footer is rebuilt here so it carries the step count, a
 * Skip, and — on the one step whose action is not a control on the page — a primary button.
 */
function renderFooter(
  popover: PopoverDOM,
  options: {
    progress: string;
    skipLabel: string;
    onSkip: () => void;
    action?: { label: string; onClick: () => void };
  },
): void {
  popover.footer.style.display = 'flex';
  popover.progress.style.display = 'block';
  popover.progress.textContent = options.progress;
  popover.footerButtons.replaceChildren();

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'crm-tour-btn crm-tour-btn-ghost';
  skip.textContent = options.skipLabel;
  skip.addEventListener('click', options.onSkip);
  popover.footerButtons.append(skip);

  if (options.action) {
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'crm-tour-btn crm-tour-btn-primary';
    action.textContent = options.action.label;
    action.addEventListener('click', options.action.onClick);
    popover.footerButtons.append(action);
  }
}
